import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { applyThemePreset, getSavedTheme, saveTheme } from '@/services/themeService'

/** 主题模式：light（亮色）/ dark（暗黑）/ system（跟随系统） */
export type ThemeMode = 'light' | 'dark' | 'system'

/** 主题 store：管理配色主题 + 亮暗模式，持久化到 localStorage */
export const useThemeStore = defineStore('theme', () => {
  const mode = ref<ThemeMode>((localStorage.getItem('theme-mode') as ThemeMode) || 'system')

  /** 当前配色主题 key（crimson / ocean / forest / sunset / royal） */
  const activeTheme = ref<string>(getSavedTheme())

  const isDark = ref(false)

  /** 应用主题到 document.documentElement */
  function applyTheme() {
    const root = document.documentElement
    if (mode.value === 'system') {
      const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      isDark.value = systemDark
    } else {
      isDark.value = mode.value === 'dark'
    }

    if (isDark.value) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }

    applyThemePreset(activeTheme.value, isDark.value)
  }

  /** 设置主题模式 */
  function setMode(newMode: ThemeMode) {
    mode.value = newMode
    localStorage.setItem('theme-mode', newMode)
    applyTheme()
  }

  /** 设置配色主题 */
  function setTheme(key: string) {
    activeTheme.value = key
    saveTheme(key)
    applyThemePreset(key, isDark.value)
  }

  /** 切换暗黑模式 */
  function toggleDark() {
    setMode(isDark.value ? 'light' : 'dark')
  }

  // 监听系统主题变化
  if (typeof window !== 'undefined') {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', () => {
      if (mode.value === 'system') {
        applyTheme()
      }
    })
  }

  // 监听 mode / activeTheme 变化
  watch(mode, applyTheme, { immediate: true })
  watch(isDark, () => {
    applyThemePreset(activeTheme.value, isDark.value)
  })

  return {
    mode,
    isDark,
    activeTheme,
    setMode,
    setTheme,
    toggleDark,
  }
})
